import type { DrawingDocument } from '../components/new-project-cad/drawingDocument';
import { LayerSetting } from '../components/project-settings/types.ts';
import { MeasurementData, TargetFunction, TargetType } from '../store/reducers/workspace.reducer.ts';
import { File, FileMetadataRequest } from './file.ts';
import { Light } from './light.ts';
import { Mesh, MeshGroup } from './mesh.ts';
import { UnitSystem } from './util.ts';

interface LampSimilarityResult {
  candidates: RankedCandidate[];
}

interface LayerAggregateResult {
  name: string;
  uuid: string;
  normId: number;
  expectedValues: ExpectedValue[];
  actualValues: ActualValue[];
}

interface ExpectedValue {
  function: TargetFunction;
  type: TargetType;
  value: number;
}

interface ActualValue {
  function: TargetFunction;
  type: TargetType;
  value: number;
}

interface Placement {
  variantId: number;
  tamanLuminaireUuid: string;
  rotationRadians?: number;
}

export interface RankedCandidate {
  lampId: number;
  satisfiesNorms: boolean;
  lumenOutput: number;
  measurementFile?: File;
  placements: Placement[];
  aggregateResults?: LayerAggregateResult[];
}

interface LampSimilarityDetailVariant {
  id: string | number;
  name: string;
}

export interface LampSimilarityDetail {
  lampId: number;
  measurementFileId?: number;
  name?: string;
  imageUrl?: string;
  measurementData?: MeasurementData[] | null;
  seriesId?: string;
  manufacturerId?: string;
  manufacturerName?: string;
  variants?: LampSimilarityDetailVariant[];
}

enum JobType {
  AUTOPOSITIONING = 'AUTOPOSITIONING',
  CALCULATION = 'CALCULATION',
  RENDER_BAKING = 'RENDER_BAKING',
  PDF_IMAGE_GENERATION = 'PDF_IMAGE_GENERATION',
}

export enum JobStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

interface Job {
  id: number;
  jobType: JobType;
  jobStatus: JobStatus;
  projectVersionId: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface Version {
  id: number;
  name: string;
  projectId: number;
  calculationJob: Job | null;
  reportImagesJob: Job | null;
  renderBakingJob: Job | null;
  autopositioningJob: Job | null;
  lampSimilarityJob: Job | null;
  lampSimilarityResult: LampSimilarityResult | null;
  lights: Light[];
  meshes: Mesh[];
  meshGroups: MeshGroup[];
  files: File[];
  measurementFileId?: number;
  measurementData?: MeasurementData[] | null;
  reportImageUrls?: Record<string, string> | null;
  wireframeObjFiles?: Array<{ url: string; lampName: string; position: [number, number, number] }> | null;
  optimisticLockKey: number;
  bakedModelUrl?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface Project {
  id: number;
  userId: number;
  name: string;
  description: string;
  calculationProgress: number;
  members: ProjectMember[];
  previewImageId: number;
  previewImageUrl: string;
  referenceImageId?: number;
  referenceImageUrl?: string;
  modelScale?: number;
  /** The drawing the project was created from; its geometry can be updated from it before calculation. */
  sourceDrawingId?: number | null;
  activeVersionId?: number;
  versions: Version[];
  modelFilesIds?: number[];
  //efficacy: number;
  //precision: number;
  //maintenance: number;
  //cameraX: number;
  //cameraY: number;
  //cameraZ: number;
  //latitude: number;
  //longitude: number;
  pdfImageGenerateStatus: PdfImageGenerateStatus;
  //files: File[];
  meshes?: Mesh[];
  //lights: Light[];
  //totalLumenOutput: number;
  createdAt: Date;
  updatedAt: Date;
}

enum PdfImageGenerateStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

interface ProjectMember {
  id: number;
  userId: number;
  fullName: string;
}

export interface NewProject {
  name: string;
  description: string;
  tolerance: number;
  maintenance: number;
  units?: UnitSystem;
  latitude: number;
  longitude: number;
  folderId?: number;
  previewImageBase64: string;
  referenceImageBase64?: string;
  modelScale?: number;
  props: FileMetadataRequest[];
  lights: Light[];
  layers: LayerSetting[];
}
export type NewProjectRequest = Omit<NewProject, 'layers'> & {
  /** The 2D drawing the model was built from (polygon flow), kept so the plan can be edited later. */
  drawing?: DrawingDocument;
  /** The drawing the project is created from (the model is a copy built from it). */
  sourceDrawingId?: number;
  meshes: Mesh[];
  meshGroups: MeshGroup[];
  props: FileMetadataRequest[];
};
